import type { GamePassUserProgressResponse } from "../../types/gamePass";
import {
  buildProgressChallengeViewModels,
  buildProgressTaskViewModels,
} from "./userProgressMappers";

/** "Task 1.2" — challenge 1, second task (FE-2300 sports bets column). */
export const formatTaskReference = (
  challengeNumber: number,
  taskNumber: number,
): string => `Task ${challengeNumber}.${taskNumber}`;

const collectTaskBetIds = (
  progressTask: ReturnType<
    typeof buildProgressTaskViewModels
  >[number]["progressTask"],
): string[] => {
  const betIds = [
    ...(progressTask.redeemed_bet_ids ?? []),
    ...(progressTask.redeemed_bets ?? []).map((bet) => bet.bet_id),
    progressTask.bet_id,
  ];
  return betIds.filter(
    (betId): betId is string =>
      typeof betId === "string" && betId.trim() !== "",
  );
};

/**
 * Reverse-maps every bet id redeemed against a Game Pass task to a
 * "Task <challenge>.<task>" reference. Challenge/task numbers are the
 * 1-based positions in the same sorted order the Game Pass tab renders
 * (challenges by sort_order, tasks by challenge_order), so the label an
 * admin sees on a bet matches what they find on the Game Pass tab.
 * First mapping wins if a bet id somehow appears under two tasks.
 */
export const buildBetTaskReferenceMap = (
  seasonProgressById: Readonly<Record<string, GamePassUserProgressResponse>>,
): ReadonlyMap<string, string> => {
  const referencesByBetId = new Map<string, string>();

  for (const progressResponse of Object.values(seasonProgressById)) {
    const challenges = buildProgressChallengeViewModels(
      progressResponse.data?.progress ?? [],
    );

    challenges.forEach((challenge, challengeIndex) => {
      const tasks = buildProgressTaskViewModels(challenge.progressEntry);

      tasks.forEach((task, taskIndex) => {
        const reference = formatTaskReference(
          challengeIndex + 1,
          taskIndex + 1,
        );

        for (const betId of collectTaskBetIds(task.progressTask)) {
          if (!referencesByBetId.has(betId)) {
            referencesByBetId.set(betId, reference);
          }
        }
      });
    });
  }

  return referencesByBetId;
};
